import { format } from 'date-fns';

import { cn } from '@/lib/utils';
import { money, monthsBetween } from './pricing-format';

const dateLabelFor = (phase) => {
	if (phase.isCurrent) return 'Today';
	if (!phase.startDate) return '';
	return format(new Date(phase.startDate), 'd MMM yyyy');
};

const descriptionFor = (phase, isLast) => {
	if (phase.isTrial) {
		return phase.endDate ? `Free until ${format(new Date(phase.endDate), 'd MMM yyyy')}` : 'Free trial';
	}
	if (isLast) return 'Then standard pricing';
	const months = monthsBetween(phase.startDate, phase.endDate);
	return months ? `${money(phase.amount, phase.currency, 0)} × ${months} months` : 'Intro pricing';
};

const amountFor = (phase) => (phase.isTrial ? money(0, phase.currency) : `${money(phase.amount, phase.currency)}/mo`);

// Add-on lines are keyed by the Stripe lookup token the channel reconcile
// writes, which is the billing product, not the dashboard channel id (one
// token can serve two channels — `meta` bills Meta Commerce and its feed).
const ADD_ON_LABELS = {
	meta: 'Meta',
	pinterest: 'Pinterest',
	tiktok: 'TikTok Shop',
	snapchat: 'Snapchat',
	ubereats: 'Uber Eats',
	deliveroo: 'Deliveroo',
	justeat: 'Just Eat',
	commercetools: 'Reservations & Local Checkout',
	'search-widget': 'Search Widget'
};

const addOnLabel = (token) =>
	ADD_ON_LABELS[token] ??
	String(token)
		.replace(/-/g, ' ')
		.replace(/\b\w/g, (m) => m.toUpperCase());

// A Stripe trial covers every item on the phase, so an add-on on a trial phase
// bills nothing until the next phase — whose row already lists it at its price.
const addOnLineFor = (addOn, phase) => {
	if (phase.isTrial) return `${addOnLabel(addOn.token)} · free during trial`;
	return addOn.variant === 'included' || addOn.amount === 0
		? `${addOnLabel(addOn.token)} · free with Pro`
		: `+ ${money(addOn.amount, addOn.currency)}/mo ${addOnLabel(addOn.token)}`;
};

/**
 * Vertical pricing schedule rendered inside the PlanCard — the headline clarity
 * win for NEA-5913. Each row is one phase from `billing.timeline` (derived from
 * the Stripe SubscriptionSchedule): Today → £1 intro → standard pricing. Markup
 * mirrors the Lovable `SubscribeSummaryCard` stepper (fresh-dashboard@main):
 * a single continuous rail with a filled square marker on the current phase and
 * hollow markers on the rest. Renders nothing when there's no timeline (plain /
 * legacy subs), so the card falls back to its single-price headline.
 *
 * A phase's `addOns` (the channel items the reconcile holds on it) render as
 * muted lines under the plan description, so a future-phase price — Meta at
 * the list rate once the free Pro year ends — is visible before it bills. The
 * right-hand amount stays the plan amount.
 */
export function PricingTimeline({ timeline }) {
	if (!Array.isArray(timeline) || timeline.length === 0) return null;

	return (
		<div className="relative pl-5 pt-1">
			<div className="absolute bottom-3 left-[5px] top-3 w-px bg-border" />
			{timeline.map((phase, index) => {
				const isLast = index === timeline.length - 1;
				const addOns = Array.isArray(phase.addOns) ? phase.addOns : [];
				return (
					<div key={phase.startDate ?? index} className="relative flex items-start justify-between gap-4 py-1.5">
						<div
							className={cn(
								'absolute -left-5 top-1.5 h-2.5 w-2.5 rounded-sm',
								phase.isCurrent ? 'bg-foreground' : 'border border-muted-foreground/50 bg-white'
							)}
						/>
						<div className="min-w-0">
							<div className="text-sm font-semibold text-foreground">{dateLabelFor(phase)}</div>
							<div className="mt-0.5 text-xs text-muted-foreground">{descriptionFor(phase, isLast)}</div>
							{addOns.length > 0 ? (
								<ul className="mt-0.5 space-y-0.5 text-xs text-muted-foreground">
									{addOns.map((addOn) => (
										<li key={addOn.lookupKey ?? addOn.token}>{addOnLineFor(addOn, phase)}</li>
									))}
								</ul>
							) : null}
						</div>
						<div className="shrink-0 text-sm font-semibold tabular-nums text-foreground">{amountFor(phase)}</div>
					</div>
				);
			})}
		</div>
	);
}
